import { OutcomeBadge } from "@/components/status";
import { subText, table, td, th } from "@/components/table";
import type { ActionLogEntry } from "@/lib/api";
import { inZone } from "@/lib/format";

/**
 * The append-only trail for one offboarding, rendered the same way wherever it
 * appears.
 *
 * Two columns exist for one reason: HROps records both the person who decided
 * and the application that acted. Entra's own audit log only ever sees the
 * service principal, so dropping either column would throw away the thing this
 * product is for.
 *
 * Oldest first — it is a trail, and a trail is read forwards. Rows are never
 * updated: a correction or a confirmation arrives as a further row, so two
 * entries for the same action are expected rather than a bug.
 */
export function ActionLog({
  entries,
  tz,
}: {
  entries: ActionLogEntry[];
  tz: string | null | undefined;
}) {
  if (entries.length === 0) {
    return (
      <p className={`${subText} mt-2`}>
        Nothing has been logged against this record yet, so nothing has been done to the account.
        Every step writes a row here as it happens.
      </p>
    );
  }

  return (
    <div className="mt-3 overflow-x-auto">
      <table className={table}>
        <thead>
          <tr>
            <th scope="col" className={th}>
              When
            </th>
            <th scope="col" className={th}>
              Action
            </th>
            <th scope="col" className={th}>
              Outcome
            </th>
            <th scope="col" className={th}>
              Person
            </th>
            <th scope="col" className={th}>
              App
            </th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => {
            const when = inZone(entry.ts, tz);
            return (
              <tr key={entry.id}>
                <td className={`${td} whitespace-nowrap`}>
                  <div className="text-fg tabular-nums">{when.time}</div>
                  <div className={subText}>{when.zone}</div>
                </td>
                <td className={td}>
                  <div className="text-fg">{entry.action}</div>
                  {entry.mechanism ? <div className={subText}>{entry.mechanism}</div> : null}
                  {entry.error ? <div className="text-danger-fg text-xs">{entry.error}</div> : null}
                </td>
                <td className={td}>
                  <OutcomeBadge outcome={entry.outcome} />
                </td>
                <td className={td}>
                  <div className="text-fg">{entry.actor_upn ?? "unknown"}</div>
                  <div className={subText}>{entry.actor_role ?? "role not recorded"}</div>
                </td>
                <td className={`${td} text-fg`}>{entry.actor_app ?? "unknown app"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
